import { Link } from 'react-router-dom';
import { useTranslation } from '@mypoz/i18n';
import { Icon, StatusBadge, cn } from '@mypoz/ui';

import { useFormat } from '@/lib/format.js';
import { getCategoryLabelKey, getSummaryKey } from '@/features/notifications/catalog.js';
import type { DashboardNotification } from '@/features/notifications/types.js';

const ROW_CLASS = 'flex items-start gap-4 px-5 py-4 sm:px-6 sm:py-5';

export interface NotificationRowProps {
  notification: DashboardNotification;
  /** Runs when the row is chosen, in either render mode — this is where it gets marked read. */
  onSelect?: () => void;
  /**
   * Where the row links to, computed by the caller (see `resolveNotificationLink`) since it can
   * depend on the notification itself, not just its category. `undefined` renders a button that
   * only runs `onSelect` instead, for a row the caller opens in place — there's nowhere to link to
   * yet (MYP-67: an unresolved PACKAGE subject) or ever (a category with no landing page at all).
   * Otherwise the row is a real link, so middle-click and open-in-new-tab work.
   */
  href?: string;
}

/**
 * One notification row, shared by the Dashboard's "Latest notifications" panel and the full
 * Notifications page — same visual language (mentor design), just a longer list on the page.
 *
 * The middle column shows the notification's `reference` (an order/tracking/account number —
 * see `notificationReference` in `@mypoz/shared`) rather than message prose: `get_msgs` sends no
 * `content` field on the real wire (MYP-112), so a reference number is the most specific thing
 * this row can honestly show without an extra fetch per row. A type whose reference is only an
 * internal id (ALIPAY, MYP-69) shows a fixed line from `getSummaryKey` instead.
 *
 * The reference takes the free space and the timestamp is pushed to the opposite edge, so rows
 * read as one aligned column of references and one of dates. Below `sm` there isn't room for
 * that, so the timestamp drops under the reference instead.
 *
 * The category badge is a fixed width with its label centred, so every message starts on the same
 * vertical line no matter how long the category name is in the active language.
 */
export function NotificationRow({ notification, onSelect, href }: NotificationRowProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const labelKey = getCategoryLabelKey(notification.type);
  const categoryLabel = labelKey ? t(labelKey) : notification.type;
  const summaryKey = getSummaryKey(notification.type);
  const headline = summaryKey ? t(summaryKey) : notification.reference;
  const timestamp = notification.msgdate ? fmt.dateTime(notification.msgdate) : null;

  const summary = (
    <>
      <span
        aria-hidden="true"
        className={cn(
          'mt-1.5 size-2 shrink-0 rounded-full',
          notification.readflag === 'N' && 'bg-danger',
        )}
      />

      <StatusBadge className="mt-0.5 w-32 shrink-0 justify-center">{categoryLabel}</StatusBadge>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-ink">{headline}</span>
        {timestamp ? (
          <span className="mt-0.5 block text-xs text-muted sm:hidden">{timestamp}</span>
        ) : null}
      </span>

      {timestamp ? (
        <span className="hidden shrink-0 text-xs whitespace-nowrap text-muted sm:block">
          {timestamp}
        </span>
      ) : null}

      <Icon name="chevron_right" size={20} className="mt-0.5 shrink-0 text-muted-soft" />
    </>
  );

  if (!href) {
    return (
      <button
        type="button"
        onClick={onSelect}
        className={cn(ROW_CLASS, 'w-full text-left transition-colors hover:bg-surface-alt')}
      >
        {summary}
      </button>
    );
  }

  return (
    <Link
      to={href}
      onClick={onSelect}
      className={cn(ROW_CLASS, 'transition-colors hover:bg-surface-alt')}
    >
      {summary}
    </Link>
  );
}
